/**
 * ============================================================================
 * DASHBOARD METRICS — SALES PERFORMANCE (single source of truth)
 * ============================================================================
 *
 * Win rate, average contract value and conversion ratio — the three numbers the
 * owner dashboard's `SalesPerformanceSection` shows.
 *
 * ## Why this file exists
 *
 * The math used to live INLINE inside `use-ceo-sales-performance-metrics.ts`, a
 * React hook — which meant it could only ever run in a browser. Putting the
 * owner glance on a phone needed the same three numbers server-side, and the
 * only options were "copy the arithmetic" (a second definition of win rate, so
 * eventually two answers) or "lift it out". This is the lift. Same shape and
 * same neighbourhood as `forecast.ts`, for the same reason
 * (`MULTI_YEAR_CONTRACT_RECOGNITION`: all forecast math lives in ONE module and
 * every surface computes through it).
 *
 * The functions here are PURE — they take the rows and the communication count
 * and return the aggregate. Which rows to read, and with what filters, stays
 * with the caller, because the read is where the two surfaces legitimately
 * differ (Apollo/`createClient` in the browser, service-role PostgREST on the
 * Bearer route).
 *
 * ## Definitions (as the web dashboard already computes them — NOT re-derived)
 *
 *  • **Win rate** = won deals ÷ ALL deals closing in the window × 100. The
 *    denominator includes LOST deals, which is the point of a win rate.
 *  • **Average contract value** = Σ revenue of won deals ÷ count of won deals,
 *    counting only deals whose revenue passes `validateDealRevenue`. A won deal
 *    with unusable revenue still counts in the DENOMINATOR — that is how the
 *    hook has always behaved, and it makes a missing revenue figure drag the
 *    average down instead of vanishing.
 *  • **Conversion ratio** = won deals ÷ deal-related communications × 100. The
 *    denominator is supplied by the caller and is already scoped to
 *    communications whose `company_id` owns a deal in the window.
 *
 * ## Two things this module deliberately does NOT do
 *
 *  1. **It does not exclude rentals.** Every forecast metric does
 *     (`aggregateForecast`'s callers filter them out first), and the recurring
 *     revenue rule says rentals are out of every revenue rollup — but the web
 *     dashboard's sales-performance hook has never filtered them, so win rate
 *     and ACV on `/admin/owner` include rental deals today. Filtering here would
 *     make the phone disagree with the laptop, which is worse than the
 *     inconsistency itself. Excluding them is a deliberate product decision for
 *     someone to take across BOTH surfaces at once; `aggregateSalesPerformance`
 *     takes whatever rows it is given, so that change is one filter at each
 *     call site when it happens.
 *  2. **It does not read.** See above.
 * ============================================================================
 */

import { isLostStage, isWonDeal } from "@/lib/constants/deal-stages";
import type { DealStage } from "@/lib/types/database/deals";
import { validateDealRevenue } from "@/lib/utils/crm/deal-data-field-validators";

/**
 * The only deal fields any number here depends on.
 *
 * Structural rather than `Pick<Deal, …>` for the reason `ForecastDealInput`
 * spells out in `forecast.ts`: `Deal` declares nullable columns as OPTIONAL,
 * which a typed PostgREST row (`string | null`) does not satisfy — and the only
 * way to pass real rows to a `Pick<Deal, …>` would be a cast that keeps
 * compiling however wrong the fields get. `Deal` satisfies this, and so does a
 * typed `deals` row.
 */
export interface SalesPerformanceDeal {
  /** Nullable, because the column is. A stage-less row counts in `totalDeals`
   * (it is a deal) and in neither `wonDeals` nor `lostDeals` — which is exactly
   * what the browser hook's `.filter(isWonDeal)` does with one today.
   * Optional as well as nullable: a codegen row types a nullable column as
   * `?: Maybe<T>`, so the property itself can be absent. */
  stage?: DealStage | null;
  /** Postgres `numeric`, so it may arrive as a string. */
  revenue?: number | string | null;
}

/** The one field the conversion ratio's SCOPE is derived from. */
export interface SalesPerformanceDealCompany {
  company_id?: string | null;
}

export interface SalesPerformanceAggregate {
  /** Won ÷ total × 100, two decimals. */
  winRate: number;
  /** Σ won revenue ÷ won count, two decimals. */
  averageContractValue: number;
  /** Won ÷ deal-related communications × 100, two decimals. */
  conversionRatio: number;
  totalDeals: number;
  wonDeals: number;
  lostDeals: number;
  /**
   * Won deals whose `revenue` failed validation. They are inside
   * `averageContractValue`'s denominator and contribute nothing to its
   * numerator, so a non-zero count here means the ACV reads LOW — surface it
   * rather than letting a bad row quietly deflate the number.
   */
  wonDealsMissingRevenue: number;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The three sales-performance numbers over a set of deals.
 *
 * @param deals Deals closing in the window — INCLUDING lost ones, which the win
 *   rate's denominator needs.
 * @param dealRelatedCommunications Count of communications tied to the window's
 *   deal companies. `0` makes the conversion ratio `0` rather than a division
 *   by zero.
 */
export function aggregateSalesPerformance(
  deals: readonly SalesPerformanceDeal[],
  dealRelatedCommunications: number
): SalesPerformanceAggregate {
  const totalDeals = deals.length;
  let wonDeals = 0;
  let lostDeals = 0;
  let wonRevenue = 0;
  let wonDealsMissingRevenue = 0;

  for (const deal of deals) {
    const stage = deal.stage;
    // `null` (no stage set) and absent (the column not projected) are the same
    // answer here: the row counts in `totalDeals` and in neither bucket.
    if (stage == null) continue;
    if (isLostStage(stage)) {
      lostDeals += 1;
      continue;
    }
    if (!isWonDeal(stage)) continue;

    wonDeals += 1;
    const { isValid, value } = validateDealRevenue(deal.revenue);
    if (isValid) wonRevenue += value;
    else wonDealsMissingRevenue += 1;
  }

  const communications = Number.isFinite(dealRelatedCommunications)
    ? Math.max(0, dealRelatedCommunications)
    : 0;

  return {
    winRate: totalDeals > 0 ? round2((wonDeals / totalDeals) * 100) : 0,
    averageContractValue: wonDeals > 0 ? round2(wonRevenue / wonDeals) : 0,
    conversionRatio: communications > 0 ? round2((wonDeals / communications) * 100) : 0,
    totalDeals,
    wonDeals,
    lostDeals,
    wonDealsMissingRevenue,
  };
}

/**
 * The distinct company ids owning a deal in the window — the scope for the
 * conversion ratio's denominator.
 *
 * Lives here beside the ratio it feeds so a caller cannot accidentally count
 * EVERY communication in the period (which would make the ratio a function of
 * total outreach volume rather than of deal-related outreach) — that is the
 * shape of the older `/api/admin/ceo/sales-performance-metrics` route, which
 * counts unscoped communications and therefore reports a different, smaller
 * conversion ratio than `/admin/owner` does.
 */
export function dealCompanyIds(
  deals: readonly SalesPerformanceDealCompany[],
): string[] {
  const ids = new Set<string>();
  for (const deal of deals) {
    if (typeof deal.company_id === "string" && deal.company_id.length > 0) {
      ids.add(deal.company_id);
    }
  }
  return Array.from(ids);
}
